import { useProgress } from '@hooks/useProgress'
import type { Entity } from '@models/Entity'
import type { GroupedOption } from '@models/GroupedOption'

import type { Project } from '../../../../aansluiting/project/types/Project'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import { useCreateActiviteitenForEntity } from '../../hooks/useCreateActiviteitenForEntity'
import { usePatchActiviteitForEntity } from '../../hooks/usePatchActiviteitForEntity'
import type { Activiteit } from '../../types/Activiteit'

import { CreateEditActiviteitForm } from './CreateEditActiviteitForm'
import type { ActiviteitFormValues } from './CreateEditActiviteitForm'

type Props = {
  activiteit?: Activiteit
  isEdit: boolean
  assignees: GroupedOption[]
  entity: Entity
  entityProps?: Partial<Project>
  onClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

export const CreateEditActiviteitDialog = ({
  activiteit,
  isEdit,
  entity,
  assignees,
  entityProps,
  onClose,
  geblokkeerdeWeken,
}: Props) => {
  const progress = useProgress()

  const { mutate: createActiviteitenForEntity, error: createError } =
    useCreateActiviteitenForEntity()
  const { mutate: patchActiviteitForEntity, error: editError } =
    usePatchActiviteitForEntity()

  const handleCancel = () => {
    onClose()
    progress.clear()
  }

  const handleSubmit = ({
    type,
    assignedTo,
    text,
    dueDate,
  }: ActiviteitFormValues) => {
    if (isEdit && activiteit) {
      /** Editing is only for a single activiteit. */
      patchActiviteitForEntity(
        {
          entity,
          activiteitInfo: {
            _id: activiteit._id,
            text,
            dueDate: dueDate as (Date & string) | undefined,
          },
        },
        { onSuccess: () => onClose() },
      )
      return
    }

    /**
     * Creating activiteiten is done one per assignee. Types Afspraak and
     * Klantcontact don't need assignees and then create a single activiteit.
     */
    const assignments =
      assignedTo.length > 0 ? assignedTo.map((a) => [a]) : [[]]
    const activiteiten = assignments.map((assignedTo) => ({
      type: type as Activiteit['type'],
      entity: {
        id: entity.id,
        type: entity.type,
        gebiedscode: entity.gebiedscode,
      },
      assignedTo,
      text,
      dueDate: dueDate as (Date & string) | undefined,
    }))

    createActiviteitenForEntity(
      {
        entity,
        activiteiten,
        progress: {
          ...progress,
          total: activiteiten.length,
          current: 1,
          error: false,
        },
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <CreateEditActiviteitForm
      activiteit={activiteit}
      isEdit={isEdit}
      entity={entity}
      assignees={assignees}
      entityProps={entityProps}
      geblokkeerdeWeken={geblokkeerdeWeken}
      error={isEdit ? editError : createError}
      progress={progress}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  )
}
